import React from "react";
import Guid from "../../../accerts/svg/guid.svg";
import iconFlr from "../../../accerts/wit/brain (1) 1.png";
import iconPrn from "../../../accerts/wit/pattern-recognition 1.png";
import iconVsp from "../../../accerts/wit/eye 1.png";
import iconQtr from "../../../accerts/wit/bar-chart 1.png";
import iconDac from "../../../accerts/wit/analysis 1.png";
import witDiagram from "../../../accerts/wit/WIT.svg";
import {
  DOMAIN_CARD_THEMES,
  formatDetailedLabel,
  formatIqRange,
  formatPercentile,
  getDetailedHeaderStyle,
  getDomainMeta,
  getStanineColor,
  getWhatYourReportSays,
  sortCompositeScores,
  WIT_CLOSING_NOTE,
  WIT_INTRO_DESCRIPTION,
  WIT_INTRO_TITLE,
  WIT_NEED_FOR_ASSESSMENT,
} from "./witUtils";
import "./WITReports.css";

const DOMAIN_ICONS = {
  FLR: iconFlr,
  PRN: iconPrn,
  VSP: iconVsp,
  QTR: iconQtr,
  DAC: iconDac,
};

const DomainCard = ({ domain }) => {
  const meta = getDomainMeta(domain.abbreviation);
  const theme = DOMAIN_CARD_THEMES[domain.abbreviation] || {
    accent: "#4a3f6b",
  };
  const iconSrc = DOMAIN_ICONS[domain.abbreviation];

  return (
    <article
      className="wit-domain-card"
      style={{ "--wit-accent": theme.accent }}
    >
      <div className="wit-domain-card__icon-graywrap">
        <div className="wit-domain-card__icon-wrap">
          {iconSrc ? (
            <img src={iconSrc} alt="" className="wit-domain-card__icon-img" />
          ) : (
            <span className="wit-domain-card__icon-fallback">
              {domain.abbreviation}
            </span>
          )}
        </div>
      </div>
      <p className="wit-domain-card__name">{meta.name}</p>
      <p className="wit-domain-card__stanine-value">{domain.stanine}</p>
      <p className="wit-domain-card__stanine-label">Stanine</p>
    </article>
  );
};

const WITReportContent = ({ advancedReport }) => {
  const compositeScores = sortCompositeScores(
    advancedReport?.composite_score_summary,
  );
  const fullScaleIq = advancedReport?.full_scale_iq || {};

  return (
    <div className="wit-report">
      <h2 className="wit-report__summary-heading">
        Hey There! Here&apos;s your Assessment Report Summary
      </h2>

      <div className="wit-areas-summary">
        <p className="wit-areas-summary__lead">You have got:</p>
        <div className="wit-areas-summary__grid">
          <div className="wit-areas-summary__item">
            <span className="wit-areas-summary__count">
              {advancedReport?.areas_of_strength ?? 0}
            </span>
            <span className="wit-areas-summary__label">
              Areas of{" "}
              <span className="wit-areas-summary__strength">Strength</span>
            </span>
          </div>
          <div className="wit-areas-summary__item">
            <span className="wit-areas-summary__count">
              {advancedReport?.areas_of_improvement ?? 0}
            </span>
            <span className="wit-areas-summary__label">
              Areas of{" "}
              <span className="wit-areas-summary__improvement">
                Improvement
              </span>
            </span>
          </div>
          <div className="wit-areas-summary__item">
            <span className="wit-areas-summary__count">
              {advancedReport?.areas_of_weakness ?? 0}
            </span>
            <span className="wit-areas-summary__label">
              Areas of{" "}
              <span className="wit-areas-summary__weakness">Weakness</span>
            </span>
          </div>
        </div>
      </div>

      <section
        className="wit-domain-section"
        aria-label="Domain stanine scores"
      >
        <div className="wit-domain-cards-row">
          <div className="wit-domain-rail" aria-hidden />
          {compositeScores.map((domain) => (
            <DomainCard key={domain.abbreviation} domain={domain} />
          ))}
        </div>
      </section>

      <div className="wit-stanine-guide MT-reportsGuide">
        <p>
          <span>Note:</span> A stanine (&quot;Standard Nine&quot;) score is a
          way to scale scores on a nine-point scale.
        </p>
        <img src={Guid} alt="Stanine scale guide" />
      </div>

      <section className="wit-intro-box">
        <h3 className="wit-intro-box__title">{WIT_INTRO_TITLE}</h3>
        <p className="wit-intro-box__text">{WIT_INTRO_DESCRIPTION}</p>
        <div className="wit-intro-box__diagram">
          <img src={witDiagram} alt="WIT cognitive domains overview" />
        </div>
      </section>

      <section className="wit-need-box">
        <div className="wit-need-box__title">Need for the Assessment</div>
        <p className="wit-need-box__text">{WIT_NEED_FOR_ASSESSMENT}</p>
      </section>

      <section className="wit-fsiq-section">
        <h3 className="wit-section-title">Full Scale IQ (FSIQ)</h3>
        <div className="wit-table wit-table--two-col">
          <div className="wit-table__head">
            <span>Percentile Rank</span>
            <span>Full Scale IQ Range*</span>
          </div>
          <div className="wit-table__row">
            <span>{formatPercentile(fullScaleIq.percentile_rank)}</span>
            <span>{formatIqRange(fullScaleIq.full_scale_iq_range)}</span>
          </div>
        </div>
      </section>

      <section className="wit-fsiq-section">
        <h3 className="wit-section-title">Full Scale IQ (FSIQ)</h3>
        <div className="wit-table wit-table--three-col">
          <div className="wit-table__head">
            <span>Full Scale IQ</span>
            <span>Stanine</span>
            <span>Full Scale IQ Range*</span>
          </div>
          {compositeScores.map((domain) => {
            const meta = getDomainMeta(domain.abbreviation);
            return (
              <div key={domain.abbreviation} className="wit-table__row">
                <span>
                  {meta.name} ({domain.abbreviation})
                </span>
                <span style={{ color: getStanineColor(domain.stanine) }}>
                  {domain.stanine}
                </span>
                <span>{formatDetailedLabel(domain.detailed)}</span>
              </div>
            );
          })}
        </div>
        <p className="wit-footnote">*IQ range with 95% confidence</p>
      </section>

      {compositeScores.map((domain) => {
        const meta = getDomainMeta(domain.abbreviation);
        const reportSays = getWhatYourReportSays(
          domain.abbreviation,
          domain.detailed,
        );
        const headerStyle = getDetailedHeaderStyle(domain.detailed);
        return (
          <section key={domain.abbreviation} className="wit-domain-detail">
            <div
              className="wit-domain-detail__header"
              style={{ background: headerStyle.background }}
            >
              <span className="wit-domain-detail__title">{meta.title}</span>
              <span
                className="wit-domain-detail__badge"
                style={{ color: headerStyle.color }}
              >
                {formatDetailedLabel(domain.detailed)}
              </span>
            </div>
            <p className="wit-domain-detail__what-is">
              <strong>What is {meta.name}?</strong>
              <br />
              {meta.whatIs}
            </p>
            <div className="wit-domain-detail__says">
              <p>
                <strong>What your report says?</strong>
                <br />
                {reportSays}
              </p>
            </div>
          </section>
        );
      })}

      <section className="wit-closing-box">
        <p>{WIT_CLOSING_NOTE}</p>
      </section>
    </div>
  );
};

export default WITReportContent;
